import React, { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc, arrayUnion, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS } from '../../styles/tokens';

export default function AddRole() {
  const { currentUser, userDoc, userRoles, refreshUserDoc } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedRole = searchParams.get('role');

  const canApplyDriver  = !userRoles.includes('driver')  || userDoc?.driverStatus  === 'rejected';
  const canApplyOrderer = !userRoles.includes('orderer') || userDoc?.ordererStatus === 'rejected';
  const targetRole =
    requestedRole === 'driver'  && canApplyDriver  ? 'driver'  :
    requestedRole === 'orderer' && canApplyOrderer ? 'orderer' :
    canApplyDriver ? 'driver' : 'orderer';
  const roleLabel  = targetRole === 'driver' ? 'sjåfør' : 'bestiller';

  const [regNumber,   setRegNumber]   = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [capacity,    setCapacity]    = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  // Guard AFTER all hooks
  if (!canApplyDriver && !canApplyOrderer) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const userRef    = doc(db, 'users', currentUser.uid);
      const profileRef = doc(db, 'publicProfiles', currentUser.uid);

      await updateDoc(userRef, {
        roles: arrayUnion(targetRole),
        ...(targetRole === 'driver'  ? { driverStatus:  'pending' } : {}),
        // Ny bestillerrolle er aktiv med en gang; en tidligere avvist bestiller må gjennom admin.
        ...(targetRole === 'orderer' ? { ordererStatus: userDoc?.ordererStatus === 'rejected' ? 'pending' : 'active' } : {}),
      });

      await updateDoc(profileRef, { roles: arrayUnion(targetRole) });

      if (targetRole === 'driver') {
        await addDoc(collection(db, 'vehicles'), {
          driverUid: currentUser.uid,
          regNumber,
          vehicleType,
          capacity: Number(capacity),
          createdAt: serverTimestamp(),
        });
      }

      await refreshUserDoc();
      navigate('/');
    } catch (err) {
      setError('Noe gikk galt. Prøv igjen.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Legg til {roleLabel}-rolle</h2>

      <div style={styles.infoBox}>
        <p style={styles.infoRow}><strong>Navn:</strong> {userDoc?.name}</p>
        <p style={styles.infoRow}><strong>E-post:</strong> {currentUser?.email}</p>
        <p style={styles.infoRow}><strong>Org.nr:</strong> {userDoc?.orgNumber ?? '—'}</p>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        {targetRole === 'driver' && (
          <>
            <label style={styles.label}>Registreringsnummer</label>
            <input style={styles.input} value={regNumber} onChange={e => setRegNumber(e.target.value)} required placeholder="AB12345" />

            <label style={styles.label}>Kjøretøytype</label>
            <input style={styles.input} value={vehicleType} onChange={e => setVehicleType(e.target.value)} required placeholder="Dumper, lastebil…" />

            <label style={styles.label}>Kapasitet (tonn)</label>
            <input style={styles.input} type="number" value={capacity} onChange={e => setCapacity(e.target.value)} required min="1" />
          </>
        )}

        {targetRole === 'orderer' && (
          <p style={styles.infoText}>
            All nødvendig informasjon er allerede registrert. Send inn for å søke om bestiller-tilgang.
          </p>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <button type="submit" style={styles.btn} disabled={loading}>
          {loading ? 'Sender søknad…' : `Søk om ${roleLabel}-rolle`}
        </button>
      </form>
    </div>
  );
}

const styles = {
  container: { maxWidth: 480, margin: '0 auto', padding: '32px 20px' },
  heading:   { fontSize: 22, fontWeight: 700, color: COLORS.primary, marginBottom: 20 },
  infoBox:   { background: '#f8f9fc', borderRadius: 8, padding: '14px 18px', marginBottom: 24 },
  infoRow:   { margin: '4px 0', fontSize: 14, color: COLORS.primary },
  form:      { display: 'flex', flexDirection: 'column', gap: 12 },
  label:     { fontSize: 13, fontWeight: 600, color: COLORS.primary },
  input:     { padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 },
  infoText:  { fontSize: 14, color: COLORS.textLight },
  error:     { color: '#e63946', fontSize: 13 },
  btn:       { marginTop: 8, padding: '13px', borderRadius: 8, background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer' },
};
